import { useEffect, useState, useCallback, useMemo } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Users2,
  Phone,
  Mail,
  Home as HomeIcon,
  Briefcase,
  Heart,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import {
  ViewModeToggle,
  SortSelect,
  useViewMode,
  useSortKey,
  sortItems,
} from "../components/ui/view-helpers";

const RELATIONS = {
  padre: { label: "Padre", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  madre: { label: "Madre", cls: "bg-pink-100 text-pink-700 border-pink-200" },
  tutore: { label: "Tutore", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  altro: { label: "Altro", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const SORT_OPTIONS = [
  { value: "name", label: "Alfabetico" },
  { value: "role", label: "Relazione" },
];

const empty = {
  first_name: "",
  last_name: "",
  relation: "padre",
  phone: "",
  email: "",
  fiscal_code: "",
  address: "",
  job: "",
  notes: "",
  player_ids: [],
};

export default function ParentsPage() {
  const { activeTeam, canEdit } = useTeams();
  const [items, setItems] = useState([]);
  const [players, setPlayers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [viewMode, changeView] = useViewMode("parentsViewMode");
  const [sortKey, setSortKey] = useSortKey("parentsSortKey", "name");

  const accessors = { role: (it) => it.relation };

  const load = useCallback(async () => {
    if (!activeTeam) return;
    const [r, p] = await Promise.all([
      api.get(`/teams/${activeTeam.id}/parents`),
      api.get(`/teams/${activeTeam.id}/players`),
    ]);
    setItems(r.data);
    setPlayers(p.data);
  }, [activeTeam]);

  useEffect(() => {
    load();
  }, [load]);

  // Build map player_id -> player and a "from-player" recap (parents not yet in registry)
  const playerMap = useMemo(() => {
    const m = {};
    players.forEach((p) => { m[p.id] = p; });
    return m;
  }, [players]);

  // Recap derived from players (contacts saved directly on player anagrafica)
  const playerContactSummary = useMemo(() => {
    return players
      .filter((p) => p.parent_name || p.parent_phone || p.parent_email)
      .map((p) => ({
        playerId: p.id,
        playerLabel: `${p.last_name} ${p.first_name}`,
        parentName: p.parent_name,
        phone: p.parent_phone,
        email: p.parent_email,
      }));
  }, [players]);

  const filtered = useMemo(() => {
    const matched = items.filter((g) => {
      if (!search) return true;
      const t = `${g.first_name} ${g.last_name} ${g.email || ""} ${g.phone || ""}`.toLowerCase();
      return t.includes(search.toLowerCase());
    });
    return sortItems(matched, sortKey, accessors);
  }, [items, search, sortKey, accessors]);

  // Grouped by child (player) — a parent linked to multiple kids appears once per kid
  const groupedByChild = useMemo(() => {
    const map = {};
    for (const g of filtered) {
      const ids = (g.player_ids || []).length ? g.player_ids : ["__none__"];
      for (const pid of ids) {
        (map[pid] = map[pid] || []).push(g);
      }
    }
    const keys = Object.keys(map);
    const ordered = [];
    // players first, "no link" last
    const players_sorted = [...players].sort((a, b) =>
      `${a.last_name || ""}`.localeCompare(b.last_name || "", "it")
    );
    for (const p of players_sorted) {
      if (map[p.id]) ordered.push({ key: p.id, player: p, parents: map[p.id] });
    }
    if (map["__none__"]) {
      ordered.push({ key: "__none__", player: null, parents: map["__none__"] });
    }
    // any keys not in players list (shouldn't happen, defensive)
    for (const k of keys) {
      if (k !== "__none__" && !players_sorted.find((p) => p.id === k)) {
        ordered.push({ key: k, player: null, parents: map[k] });
      }
    }
    return ordered;
  }, [filtered, players]);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("Nome e cognome richiesti");
      return;
    }
    const payload = { ...form, team_id: activeTeam.id };
    if (editingId) await api.put(`/parents/${editingId}`, payload);
    else await api.post(`/parents`, payload);
    toast.success("Genitore salvato");
    setOpen(false);
    setForm(empty);
    setEditingId(null);
    load();
  };

  const handleNew = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const handleEdit = (g) => {
    setForm({
      first_name: g.first_name || "",
      last_name: g.last_name || "",
      relation: g.relation || "padre",
      phone: g.phone || "",
      email: g.email || "",
      fiscal_code: g.fiscal_code || "",
      address: g.address || "",
      job: g.job || "",
      notes: g.notes || "",
      player_ids: g.player_ids || [],
    });
    setEditingId(g.id);
    setOpen(true);
  };
  const handleDelete = async (g) => {
    if (!window.confirm(`Eliminare ${g.first_name} ${g.last_name}?`)) return;
    await api.delete(`/parents/${g.id}`);
    load();
  };

  const togglePlayer = (pid) => {
    setForm((f) => ({
      ...f,
      player_ids: f.player_ids.includes(pid)
        ? f.player_ids.filter((x) => x !== pid)
        : [...f.player_ids, pid],
    }));
  };

  return (
    <div className="space-y-6" data-testid="parents-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Anagrafica</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">
            Genitori
          </h1>
          <p className="text-slate-500">
            {items.length} contatti registrati
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <ViewModeToggle
            value={viewMode}
            onChange={changeView}
            groupLabel="Per figlio"
            testid="parents-view-toggle"
          />
          <SortSelect
            value={sortKey}
            onChange={setSortKey}
            options={SORT_OPTIONS}
            testid="parents-sort"
          />
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Cerca nome, telefono, email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-64"
              data-testid="parents-search"
            />
          </div>
          {canEdit && (
            <Button
              onClick={handleNew}
              className="rounded-full bg-sky-500 hover:bg-sky-600"
              data-testid="new-parent-btn"
            >
              <Plus size={16} className="mr-1" /> Nuovo genitore
            </Button>
          )}
        </div>
      </div>

      {viewMode === "cards" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((g) => (
            <ParentCard key={g.id} g={g} playerMap={playerMap} canEdit={canEdit} onEdit={handleEdit} onDelete={handleDelete} />
          ))}
          {filtered.length === 0 && (
            <Card className="p-10 col-span-full text-center text-slate-500 border-dashed">
              <Users2 size={28} className="mx-auto text-slate-300 mb-2" />
              {items.length === 0
                ? "Nessun genitore registrato. Aggiungi il primo o consulta il riepilogo qui sotto."
                : "Nessun genitore corrisponde alla ricerca."}
            </Card>
          )}
        </div>
      )}

      {viewMode === "list" && (
        <Card className="overflow-hidden" data-testid="parents-list-view">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500">Nessun genitore.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Nome</th>
                    <th className="px-4 py-3 text-left">Relazione</th>
                    <th className="px-4 py-3 text-left">Figlio/a</th>
                    <th className="px-4 py-3 text-left">Contatti</th>
                    {canEdit && <th className="px-4 py-3 text-right">Azioni</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((g) => {
                    const rel = RELATIONS[g.relation] || RELATIONS.altro;
                    const children = (g.player_ids || []).map((pid) => playerMap[pid]).filter(Boolean);
                    return (
                      <tr key={g.id} className="hover:bg-slate-50" data-testid={`parent-row-${g.id}`}>
                        <td className="px-4 py-3 font-medium">{g.last_name} {g.first_name}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={`${rel.cls} font-medium`}>
                            {rel.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">
                          {children.length === 0 ? (
                            <span className="text-slate-400 italic">non collegato</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {children.map((p) => (
                                <span key={p.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-700">
                                  <Heart size={10} /> {p.last_name} {p.first_name}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600 space-x-2">
                          {g.phone && <span>📞 {g.phone}</span>}
                          {g.email && <span>✉ {g.email}</span>}
                        </td>
                        {canEdit && (
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <Button size="sm" variant="ghost" onClick={() => handleEdit(g)}>
                              <Pencil size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(g)} className="text-red-600 hover:bg-red-50">
                              <Trash2 size={14} />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {viewMode === "group" && (
        <div className="space-y-6" data-testid="parents-group-view">
          {groupedByChild.length === 0 && (
            <Card className="p-10 text-center text-slate-500 border-dashed">Nessun genitore.</Card>
          )}
          {groupedByChild.map(({ key, player, parents: list }) => (
            <div key={key}>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-sm font-bold">
                  <Heart size={11} className="mr-1" />
                  {player ? `${player.last_name} ${player.first_name}` : "Non collegati a un giocatore"}
                </Badge>
                <span className="text-xs text-slate-400">
                  {list.length} {list.length === 1 ? "genitore" : "genitori"}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((g) => (
                  <ParentCard key={g.id} g={g} playerMap={playerMap} canEdit={canEdit} onEdit={handleEdit} onDelete={handleDelete} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recap from player anagrafica (read-only contact already stored on player) */}
      {playerContactSummary.length > 0 && (
        <Card className="p-5 border-slate-200 bg-slate-50/40">
          <div className="label-eyebrow mb-3 flex items-center gap-2">
            <Users2 size={14} /> Contatti registrati sull&apos;anagrafica giocatore
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {playerContactSummary.map((c) => (
              <div key={c.playerId} className="p-3 rounded-lg bg-white border border-slate-200 text-xs">
                <div className="font-semibold text-slate-700 truncate">{c.parentName || "Genitore"}</div>
                <div className="text-slate-400 truncate">Figlio: {c.playerLabel}</div>
                <div className="text-slate-500 truncate">{c.phone || c.email}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica genitore" : "Nuovo genitore"}</DialogTitle>
            <DialogDescription className="sr-only">
              Inserisci i dati anagrafici del genitore o tutore e collegalo ai figli.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nome</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="parent-firstname" />
            </div>
            <div>
              <Label>Cognome</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="parent-lastname" />
            </div>
            <div>
              <Label>Relazione</Label>
              <Select value={form.relation} onValueChange={(v) => setForm({ ...form, relation: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(RELATIONS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Lavoro</Label>
              <Input value={form.job} onChange={(e) => setForm({ ...form, job: e.target.value })} />
            </div>
            <div>
              <Label>Telefono</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39 333 ..." />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Codice fiscale</Label>
              <Input value={form.fiscal_code} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label>Indirizzo</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Figli collegati</Label>
              <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-slate-200 p-2 space-y-1">
                {players.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 rounded p-1">
                    <input
                      type="checkbox"
                      checked={form.player_ids.includes(p.id)}
                      onChange={() => togglePlayer(p.id)}
                    />
                    <span>{p.last_name} {p.first_name}</span>
                  </label>
                ))}
                {players.length === 0 && <p className="text-xs text-slate-400">Nessun giocatore in anagrafica</p>}
              </div>
            </div>
            <div className="col-span-2">
              <Label>Note</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} className="bg-sky-500 hover:bg-sky-600" data-testid="save-parent-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ParentCard({ g, playerMap, canEdit, onEdit, onDelete }) {
  const rel = RELATIONS[g.relation] || RELATIONS.altro;
  const children = (g.player_ids || []).map((pid) => playerMap[pid]).filter(Boolean);
  return (
    <Card className="p-5 border-slate-200" data-testid={`parent-${g.id}`}>
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-2xl bg-violet-50 text-violet-600 flex items-center justify-center font-heading font-bold shrink-0">
          {(g.first_name?.[0] || "") + (g.last_name?.[0] || "")}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold truncate">
            {g.last_name} {g.first_name}
          </div>
          <Badge variant="outline" className={`mt-1 ${rel.cls} font-medium`}>
            {rel.label}
          </Badge>
        </div>
        {canEdit && (
          <div className="flex flex-col gap-0.5">
            <Button size="icon" variant="ghost" onClick={() => onEdit(g)}>
              <Pencil size={14} />
            </Button>
            <Button size="icon" variant="ghost" onClick={() => onDelete(g)}>
              <Trash2 size={14} className="text-red-500" />
            </Button>
          </div>
        )}
      </div>
      {children.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {children.map((p) => (
            <span key={p.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-xs">
              <Heart size={10} /> {p.last_name} {p.first_name}
            </span>
          ))}
        </div>
      )}
      <div className="mt-3 space-y-1 text-xs">
        {g.phone && (
          <a href={`tel:${g.phone}`} className="flex items-center gap-1.5 text-slate-700 hover:text-sky-600">
            <Phone size={11} /> {g.phone}
          </a>
        )}
        {g.email && (
          <a href={`mailto:${g.email}`} className="flex items-center gap-1.5 text-slate-700 hover:text-sky-600 truncate">
            <Mail size={11} /> {g.email}
          </a>
        )}
        {g.address && (
          <div className="flex items-center gap-1.5 text-slate-500">
            <HomeIcon size={11} /> {g.address}
          </div>
        )}
        {g.job && (
          <div className="flex items-center gap-1.5 text-slate-500">
            <Briefcase size={11} /> {g.job}
          </div>
        )}
      </div>
    </Card>
  );
}